import { Avatar, Button, Divider, Modal, Paper, Typography } from '@material-ui/core';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import React, { Dispatch, SetStateAction } from 'react';
import { Link } from 'react-router-dom';

import { VolunteerReward, VolunteerRewardApprovalStatus } from 'generated/graphql';
import { convertDuration } from 'utils/convertDuration';
import { CurrencyCode, currencyFormat } from 'utils/currencyFormat';
import { formatFullName } from 'utils/formatFullName';

import { StepContentItem, StepItem } from './StepItem';
import { FilterStatus } from './Filters';

const styles = {
  modal: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    paddingLeft: 32,
    paddingRight: 32,
    paddingTop: 16,
    paddingBottom: 16,
    width: 600,
    display: 'flex',
    flexDirection: 'column',
  },
  divider: {
    margin: '16px 0',
  },
  stepsContainer: {
    width: 'fit-content',
    alignSelf: 'center',
    paddingTop: 16,
    paddingBottom: 16,
  },
  avatar: {
    width: 32,
    height: 32,
    float: 'left',
    marginRight: 10,
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flex: 1,
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  userDetails: {
    display: 'inline-block',
  },
} as const;

const getStepTwoTitle = (status?: FilterStatus) => {
  switch (status) {
    case VolunteerRewardApprovalStatus.Approved:
      return 'Volunteer Grant Approved';

    case VolunteerRewardApprovalStatus.Rejected:
      return 'Volunteer Grant Rejected';

    case 'NON_DISBURSABLE':
      return 'Volunteer Grant Non-disbursable';

    default:
      return 'Request for Volunteer Grants';
  }
};

export const VolunteerRewardDetailModal = ({
  showModal,
  setShowModal,
  volunteerRewardDetails,
  handleApproveVolunteerReward,
  handleRejectVolunteerReward,
  filterStatus,
}: {
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  volunteerRewardDetails?: VolunteerReward;
  handleApproveVolunteerReward: (id: string) => void;
  handleRejectVolunteerReward: (id: string) => void;
  filterStatus: FilterStatus;
}) => {
  if (!volunteerRewardDetails) {
    return null;
  }

  const { id, User, volunteerDuration, Deed, amount, currency, VolunteerTime } =
    volunteerRewardDetails;

  const fullName = formatFullName(User.fullName);
  const deedName = Deed?.name;
  // We don't use volunteerReward.nonprofit, because that's the np where the donation goes to. Admins are more interested of the np the user volunteered for */
  const nonprofitName = VolunteerTime.Nonprofit?.name;
  const { notes } = VolunteerTime;
  const formattedCreatedAt = VolunteerTime.Nonprofit?.createdAt
    ? moment(VolunteerTime.Nonprofit.createdAt).format('MMM DD, YYYY')
    : '-';

  const handleReject = () => {
    handleRejectVolunteerReward(id);
    setShowModal(false);
  };
  const handleApprove = () => {
    handleApproveVolunteerReward(id);
    setShowModal(false);
  };

  const showActions = filterStatus === VolunteerRewardApprovalStatus.Pending;

  return (
    <Modal
      open={showModal}
      onClose={() => setShowModal(false)}
      style={styles.modal}
      disableAutoFocus
    >
      <Paper style={styles.paper}>
        <div style={styles.modalHeader}>
          <div style={styles.userInfo}>
            <Avatar
              alt={fullName}
              src={User?.mainPicture?.replace('http://', 'https://')}
              style={styles.avatar}
            />
            <div style={{ display: 'inline-block' }}>
              {fullName}
              <Typography component="p" style={{ fontSize: 11 }}>
                <Link className="hoverable" to={`volunteers/${User.id}`}>
                  View Profile
                </Link>
              </Typography>
            </div>
          </div>
          <CloseIcon style={{ cursor: 'pointer' }} onClick={() => setShowModal(false)} />
        </div>
        <Divider style={styles.divider} />
        <div style={styles.stepsContainer}>
          <StepItem title="Approved Volunteer Hours" stepNumber={1} checked>
            <>
              {deedName && <StepContentItem title="Volunteer event:" content={deedName} />}
              <StepContentItem title="Organization:" content={nonprofitName} />
              <StepContentItem
                title="Volunteer hours:"
                content={convertDuration(volunteerDuration)}
              />
            </>
          </StepItem>
          <StepItem
            title={getStepTwoTitle(filterStatus)}
            stepNumber={filterStatus === VolunteerRewardApprovalStatus.Pending ? 2 : undefined}
            end
            status={filterStatus}
          >
            <>
              <StepContentItem
                title="Volunteer Awards Amount:"
                content={amount && currencyFormat(amount, currency as CurrencyCode)}
              />
              <StepContentItem title="Volunteer Hours Submission:" content={formattedCreatedAt} />
              <StepContentItem title="Note:" content={notes} />
            </>
          </StepItem>
        </div>
        {showActions && (
          <div style={{ display: 'flex', justifyContent: 'stretch' }}>
            <Button
              style={{ marginRight: 16, display: 'flex', flex: 'auto' }}
              onClick={handleReject}
            >
              Reject
              <BlockIcon style={{ marginLeft: 5 }} />
            </Button>
            <Button
              style={{ display: 'flex', flex: 'auto' }}
              color="primary"
              variant="contained"
              onClick={handleApprove}
            >
              Approve Volunteer Grant
              <CheckIcon style={{ marginLeft: 5 }} />
            </Button>
          </div>
        )}
      </Paper>
    </Modal>
  );
};
